import { Alert, Avatar, Box, Button, Checkbox, Paper, Skeleton, Stack, Typography } from '@mui/material'
import {
  CompanySnsConnectionProvider,
  useCreateCompanySnsConnectionMutation,
  useGetLongLiveAccessTokenLazyQuery,
  useViewerQuery,
} from '~/generated/graphql'
import { enqueueSnackbar } from 'notistack'
import { FC, useState } from 'react'
import { FBPageAndIGUser, FBPages, getFacebookAndIGUser, getFacebookPages } from '~/api/FacebookRepository'
import { staticPath } from '~/lib/$path'
import { ZoomImageList } from '../ConnectInstagramAccount/ZoomImageList'
import { FacebookLoginButton } from '../FacebookLoginButton'
import { DateTime } from 'luxon'
import { logger } from '~/lib/logger'

export const Finish: FC = () => {
  const { data: viewerData, loading: viewerLoading, refetch } = useViewerQuery()
  const [getLongLiveToken] = useGetLongLiveAccessTokenLazyQuery()
  const [createSnsConnection] = useCreateCompanySnsConnectionMutation()
  const [checkedPageIds, setCheckedPageIds] = useState<string[]>([])

  const [isIGUserLoading, setIsIGUserLoading] = useState(false)
  const [isFBDataLoaded, setIsFBDataLoaded] = useState(false)
  const [dataAccessExpirationTime, setDataAccessExpirationTime] = useState<null | number>(null)

  // fbPagesとfbAndUserDataのステートを定義
  const [fbPages, setFbPages] = useState<FBPages>({ data: [] })
  const [fbAndUserData, setFbAndUserData] = useState<FBPageAndIGUser[]>([])

  if (viewerLoading) return <Skeleton variant="rectangular" height={'100vh'} />
  if (!viewerData) {
    return <Alert severity="error">閲覧権限がありません。</Alert>
  }

  const handleCreateSnsConnections = async () => {
    if (!fbAndUserData.length || !checkedPageIds.length || !dataAccessExpirationTime) return
    const checkedData = fbAndUserData.filter((x) => checkedPageIds.includes(x.fbPage.id))
    try {
      await Promise.all(
        checkedData.map((item) => {
          return createSnsConnection({
            variables: {
              input: {
                uid: item.igUser.id,
                imageUrl: item.igUser.profile_picture_url,
                name: item.igUser.username,
                token: item.fbPage.access_token,
                provider: CompanySnsConnectionProvider.Instagram,
                nextTokenUpdateAt: DateTime.fromSeconds(dataAccessExpirationTime).toISO() ?? '',
              },
            },
          })
        }),
      )
      setCheckedPageIds([])
      refetch()
      enqueueSnackbar('アカウントを連携しました', { variant: 'success' })
    } catch (err: unknown) {
      const castErr = (e: unknown): e is { message: string } => typeof e === 'object' && e !== null && 'message' in e
      if (castErr(err)) {
        enqueueSnackbar(err.message, { variant: 'error' })
      }
    }
  }

  const connectedData = viewerData.viewer.company?.snsConnections
  const onFacebookLoggedIn = ({ authResponse }: fb.StatusResponse) => {
    // FIXME: FB.loginに非同期コールバックを渡すと原因不明のエラーが出るため、暫定対応として非同期即時関数でかこう
    ;(async () => {
      try {
        // NOTE: 認証時に開くWindowを閉じるとnullで返る

        // TypeError: null is not an object (evaluating 't.accessToken') が発生するため
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!authResponse?.accessToken || !authResponse.data_access_expiration_time) return
        const { accessToken, data_access_expiration_time } = authResponse
        setIsIGUserLoading(true)
        const { data: tokenInfo } = await getLongLiveToken({
          variables: { shortLiveAccessToken: accessToken },
        })

        const pages = await getFacebookPages(tokenInfo?.facebookLongLiveAccessToken ?? '')
        const userData = await getFacebookAndIGUser(pages, accessToken)

        // 取得したデータをステートにセット
        setFbPages(pages)
        setFbAndUserData(userData)

        // 初期表示＆ページを再レンダリングした際に表示をさせないステート
        setIsFBDataLoaded(true)

        setCheckedPageIds(
          fbAndUserData
            .map(({ fbPage }) => fbPage.id)
            .filter((x) => connectedData?.some((connected) => connected.uid === x)),
        )

        setDataAccessExpirationTime(data_access_expiration_time)
      } catch (err) {
        setFbAndUserData([])

        const castErr = (e: unknown): e is { message: string } => typeof e === 'object' && e !== null && 'message' in e
        if (castErr(err)) {
          enqueueSnackbar(err.message, { variant: 'error' })
        }

        logger.error(err as Error)
      } finally {
        setIsIGUserLoading(false)
      }
    })()
  }

  return (
    <Stack spacing={2}>
      <Typography>Instagram連携をするために、下記画像を参照しながらFacebook認証をしてください。</Typography>

      {!!connectedData?.length && (
        <Box sx={{ my: 1 }}>
          <Box>
            <Typography variant="body2" sx={{ my: 1, fontWeight: 'bold' }}>
              連携済みアカウント
            </Typography>

            {connectedData.map((snsConnection) => {
              return (
                <Paper
                  elevation={1}
                  key={snsConnection.uid}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    my: 1,
                    p: 1,
                  }}
                >
                  <Avatar src={snsConnection.imageUrl ?? ''} />
                  <Typography variant="body2">{snsConnection.name}</Typography>
                </Paper>
              )
            })}
          </Box>
        </Box>
      )}

      <Stack spacing={0.5} pt={0.5}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          下記ボタンからFacebook認証を開始してください。新規ウィンドウが立ち上がりますので、画像を参照しながらFacebook認証をします。
        </Typography>
        <Box>
          <FacebookLoginButton onFacebookLoggedIn={onFacebookLoggedIn} />
        </Box>
        <Stack spacing={1} pt={2}>
          <Typography variant="body2" sx={{ mt: 2 }}>
            紐付けたいInstagramアカウント、Facebookページを選択後、完了をクリックします。
          </Typography>
          <ZoomImageList
            paths={[
              staticPath.facebook_connect_steps.facebook_auth.step_0_webp,
              staticPath.facebook_connect_steps.facebook_auth.step_1_webp,
              staticPath.facebook_connect_steps.facebook_auth.step_2_webp,
              staticPath.facebook_connect_steps.facebook_auth.step_3_webp,
            ]}
          />
        </Stack>
      </Stack>

      <Stack sx={{ pt: 2 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Facebookログイン後、紐付けたいアカウントを選択し、「選択したアカウントを連携する」をクリックすれば連携完了です。
        </Typography>
        {isIGUserLoading ? (
          <Skeleton variant="rectangular" width={'100%'} height={'40px'} sx={{ mb: 2 }} />
        ) : !isFBDataLoaded ? null : !fbPages.data.length ? (
          // facebookページ作成無し
          <Box pb={2}>
            <Alert severity="warning">
              Facebookページを作成していない、もしくは選択されていません。Step:4/5を参照し、ページを作成してください。
            </Alert>
          </Box>
        ) : !fbAndUserData.length ? (
          // インスタプロアカウント設定無し
          <Box pb={2}>
            <Alert severity="warning">
              FacebookページにリンクされたInstagramプロアカウントが見つかりません。
              <br />
              以下の点を確認してください。
              <br />
              ・Instagramアカウントはプロアカウントになっているか（Step:3/5）
              <br />
              ・FacebookページとInstagramアカウントとのリンクが正常に行われているか（Step:4/5）
            </Alert>
          </Box>
        ) : (
          fbAndUserData.map((item) => {
            return (
              <Paper elevation={1} key={item.fbPage.id} sx={{ p: 1, my: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {connectedData?.some((x) => x.uid === item.igUser.id) ? (
                    // すでに連携済みのアカウントを出す場合
                    <Typography variant="caption" style={{ color: 'red' }}>
                      連携済み
                    </Typography>
                  ) : (
                    <Box>
                      <Checkbox
                        checked={checkedPageIds.includes(item.fbPage.id)}
                        onChange={(e) => {
                          setCheckedPageIds((prev) => {
                            return e.target.checked
                              ? [...prev, item.fbPage.id]
                              : prev.filter((x) => x !== item.fbPage.id)
                          })
                        }}
                      />
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography sx={{ fontWeight: 'bold' }}>{item.fbPage.name}</Typography>
                      <Avatar src={item.igUser.profile_picture_url} />
                      <Box>
                        <Typography variant="body2">@{item.igUser.username}</Typography>
                        <Typography variant="caption">{item.igUser.name}</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {item.igUser.followers_count} フォロワー
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            )
          })
        )}

        <Box>
          <Button variant="contained" onClick={handleCreateSnsConnections} disabled={!checkedPageIds.length}>
            選択したアカウントを連携する
          </Button>
        </Box>
      </Stack>
    </Stack>
  )
}
